import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useCart } from "@/lib/cart";
import { useAuth } from "@/lib/auth";
import { formatINR, api } from "@/lib/api";
import { Trash2, Gift, Sparkles } from "lucide-react";
import { toast } from "sonner";
import RecommendationStrip from "@/components/RecommendationStrip";

export default function Cart() {
  const { cart, remove, setQty } = useCart() || { cart: { items: [] } };
  const { user } = useAuth();
  const [code, setCode] = useState("");
  const [coupon, setCoupon] = useState(null);
  const [bundle, setBundle] = useState(null);
  const hasFabric = (cart.items || []).some((it) => it.is_fabric);

  useEffect(() => {
    if (cart.subtotal > 0) {
      api.get(`/bundles/best?subtotal=${cart.subtotal}`).then(({ data }) => setBundle(data.applicable ? data : null)).catch(() => {});
    } else { setBundle(null); }
  }, [cart.subtotal]);

  const applyCoupon = async () => {
    try { const { data } = await api.post("/coupons/validate", { code, subtotal: cart.subtotal }); setCoupon(data); toast.success(`Coupon applied: −${formatINR(data.discount)}`); }
    catch (e) { toast.error("Invalid coupon"); }
  };

  if (!user) return <div className="sq-container py-20"><Link to="/auth" className="sq-btn-primary">Sign in to view your cart</Link></div>;

  const bundleDiscount = bundle?.discount || 0;
  const grandTotal = cart.total - (coupon?.discount || 0) - bundleDiscount;

  return (
    <main className="sq-container py-12" data-testid="cart-page">
      <h1 className="font-display text-5xl">Your Cart</h1>

      {bundle && (
        <div className="mt-6 border-2 border-squire-jeans bg-[#F5F5F5] p-5 flex items-center gap-4" data-testid="cart-bundle-banner">
          <Sparkles className="text-squire-jeans" />
          <div className="flex-1">
            <div className="sq-eyebrow text-squire-jeans">Bundle Unlocked</div>
            <div className="font-display text-xl mt-1">{bundle.bundle.name}</div>
            <div className="text-sm text-[#4B5563]">{bundle.bundle.description}</div>
          </div>
          {bundle.free_product && (
            <div className="flex items-center gap-3">
              <img src={bundle.free_product.images?.[0]} alt={bundle.free_product.name} className="w-14 h-14 object-cover" />
              <div className="text-xs"><Gift size={12} className="inline mr-1" /> Free {bundle.free_product.name}</div>
            </div>
          )}
        </div>
      )}

      {cart.items.length === 0 ? (
        <div className="mt-10 text-[#4B5563]">Your cart is empty. <Link to="/fabrics" className="underline">Start shopping</Link></div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12 mt-10">
          <div className="lg:col-span-2 space-y-4" data-testid="cart-items">
            {cart.items.map((it, i) => (
              <div key={i} className="flex gap-5 border-b border-black/10 pb-5">
                <img src={it.product.images?.[0]} alt="" className="w-24 h-32 object-cover" />
                <div className="flex-1">
                  <div className="font-display text-xl">{it.product.name}</div>
                  <div className="sq-eyebrow mt-1">{it.product.id}{it.is_fabric ? "" : ` · ${it.size || "—"} · ${it.color || "—"}`}</div>
                  <div className="text-xs text-[#4B5563] mt-1">{formatINR(it.unit_price)} {it.is_fabric ? "/ mtr" : "each"}</div>
                  <div className="mt-3 inline-flex items-center border border-black/20 rounded">
                    <button data-testid={`cart-dec-${i}`} onClick={() => setQty(it.product_id, it.quantity - 1, { size: it.size, color: it.color })} disabled={it.quantity <= 1} className="w-8 h-8 grid place-items-center disabled:opacity-30 hover:bg-black/5">−</button>
                    <span className="px-3 text-sm min-w-[64px] text-center" data-testid={`cart-qty-${i}`}>{it.quantity} {it.is_fabric ? "m" : ""}</span>
                    <button data-testid={`cart-inc-${i}`} onClick={() => setQty(it.product_id, it.quantity + 1, { size: it.size, color: it.color })} className="w-8 h-8 grid place-items-center hover:bg-black/5">+</button>
                  </div>
                  {it.is_fabric && <div className="text-[11px] text-[#9CA3AF] mt-1">{it.quantity} m × {formatINR(it.unit_price)}/mtr</div>}
                </div>
                <div className="text-right">
                  <div className="font-semibold">{formatINR(it.line_total)}</div>
                  <button data-testid={`cart-remove-${i}`} onClick={() => remove(it.product_id, { size: it.size, color: it.color })} className="mt-3 text-xs text-[#4B5563] hover:text-squire-jeans flex items-center gap-1"><Trash2 size={12} /> Remove</button>
                </div>
              </div>
            ))}

            {hasFabric && (
              <Link to="/bespoke" data-testid="cart-stitch-cta" className="block bg-black text-white rounded-xl p-5 flex items-center justify-between gap-4 hover:bg-squire-jeans transition-colors">
                <div>
                  <div className="sq-eyebrow text-white/60">Bought the cloth?</div>
                  <div className="font-display text-xl md:text-2xl mt-1">Get it stitched — turn your fabric into a bespoke shirt, trouser & more →</div>
                </div>
                <Sparkles className="shrink-0" />
              </Link>
            )}
          </div>
          <aside className="border border-black/10 p-6 h-fit" data-testid="cart-summary">
            <div className="sq-eyebrow">Summary</div>
            <div className="mt-4 space-y-2 text-sm">
              <div className="flex justify-between"><span>Subtotal</span><span>{formatINR(cart.subtotal)}</span></div>
              {coupon && <div className="flex justify-between text-squire-jeans"><span>Coupon ({coupon.coupon.code})</span><span>−{formatINR(coupon.discount)}</span></div>}
              {bundleDiscount > 0 && <div className="flex justify-between text-squire-jeans"><span>Bundle</span><span>−{formatINR(bundleDiscount)}</span></div>}
              <div className="flex justify-between"><span>Tax (5%)</span><span>{formatINR(cart.tax)}</span></div>
              <div className="flex justify-between font-semibold pt-3 border-t border-black/10"><span>Total</span><span>{formatINR(grandTotal)}</span></div>
            </div>
            <div className="mt-6 flex gap-2">
              <input data-testid="cart-coupon" value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder="COUPON" className="flex-1 bg-transparent border-b border-black/30 outline-none py-1 text-sm" />
              <button data-testid="cart-coupon-apply" onClick={applyCoupon} className="sq-btn-outline !py-2 !px-3 !text-[10px]">Apply</button>
            </div>
            <Link to="/checkout" data-testid="cart-checkout" className="sq-btn-primary w-full mt-6">Proceed to Checkout</Link>
            {user?.first_order_credit > 0 && <p className="text-xs text-squire-jeans mt-3">First-order credit ₹{user.first_order_credit} will be auto-applied at checkout.</p>}
          </aside>
        </div>
      )}
      {cart.items.length > 0 && <RecommendationStrip context="cart" title="Complete the look" testid="cart-recs" />}
    </main>
  );
}
